/**
 * HM-442 cluster 23 Manutenzione Guasti: drawer dettaglio guasto.
 * - Mostra dettaglio camera + nota + cronologia (open/resolved).
 * - Permette edit nota se status=open (admin/reception lato backend).
 * - Bottone "Risolvi" → PATCH status=resolved.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  type MaintenanceIssue,
  getIssue,
  resolveIssue,
  updateIssueNote,
} from '../../api/maintenance.js';
import type { RoomDb } from '../../api/rooms.js';

function formatItalianDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()} ${hh}:${min}`;
}

export function IssueDetailDrawer({
  issueId,
  rooms,
  onClose,
}: {
  issueId: string;
  rooms: RoomDb[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['maintenance', 'detail', issueId],
    queryFn: () => getIssue(issueId),
    staleTime: 5_000,
  });

  const issue: MaintenanceIssue | undefined = detailQuery.data?.issue;
  const room = issue ? (rooms.find((r) => r.id === issue.roomId) ?? null) : null;

  const [editing, setEditing] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (issue) setDraftNote(issue.note);
  }, [issue]);

  const noteMutation = useMutation({
    mutationFn: (note: string) => updateIssueNote(issueId, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      setEditing(false);
      setError(null);
    },
    onError: (err: Error) => setError(err.message ?? 'Errore aggiornamento'),
  });

  const resolveMutation = useMutation({
    mutationFn: () => resolveIssue(issueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      setError(null);
    },
    onError: (err: Error) => setError(err.message ?? 'Errore risoluzione'),
  });

  function handleSaveNote() {
    const trimmed = draftNote.trim();
    if (trimmed.length < 1) {
      setError('La descrizione non puo essere vuota');
      return;
    }
    noteMutation.mutate(trimmed);
  }

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        data-testid="manutenzione-drawer-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(45, 36, 29, 0.4)',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          zIndex: 40,
        }}
      />
      {/* Drawer */}
      {/* biome-ignore lint/a11y/useSemanticElements: pattern overlay div coerente con GuestDetailDrawer / BookingDetailDrawer; <dialog> nativo richiede show()/close() API non adottata in v2.6. */}
      <aside
        role="dialog"
        aria-label="Dettaglio guasto"
        data-testid="manutenzione-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: 'var(--app-vh, 100vh)',
          width: 'min(420px, 100vw)',
          background: '#FFF',
          boxShadow: '-8px 0 24px rgba(45, 36, 29, 0.16)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #E8DECF',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#7A6957',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Guasto
            </div>
            {issue && (
              <div
                style={{ fontSize: 18, fontWeight: 600, color: '#3D2F24' }}
                data-testid="manutenzione-drawer-room-code"
              >
                {room?.code ?? '—'}
                {room && (
                  <span style={{ fontSize: 12, color: '#7A6957', marginLeft: 8 }}>
                    {room.building} · {room.floor}
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="manutenzione-drawer-close"
            aria-label="Chiudi pannello"
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              color: '#7A6957',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            ×
          </button>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {detailQuery.isLoading && <p style={{ color: '#7A6957' }}>Caricamento…</p>}
          {!detailQuery.isLoading && issue && (
            <>
              <div style={{ marginBottom: 16 }}>
                <span
                  data-testid="manutenzione-drawer-status"
                  style={{
                    display: 'inline-block',
                    padding: '4px 12px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600,
                    background: issue.status === 'open' ? '#FFE5DC' : '#E4EDDE',
                    color: issue.status === 'open' ? '#A8453A' : '#4A6B3A',
                  }}
                >
                  {issue.status === 'open' ? 'Aperto' : 'Risolto'}
                </span>
              </div>

              <section style={{ marginBottom: 24 }}>
                <div style={sectionLabel}>Descrizione</div>
                {editing ? (
                  <>
                    <textarea
                      value={draftNote}
                      onChange={(e) => setDraftNote(e.target.value)}
                      rows={6}
                      maxLength={2000}
                      data-testid="manutenzione-drawer-note-input"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: '1px solid #D4C4B0',
                        fontSize: 14,
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        color: '#3D2F24',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        type="button"
                        onClick={handleSaveNote}
                        disabled={noteMutation.isPending}
                        data-testid="manutenzione-drawer-note-save"
                        style={primaryBtn}
                      >
                        {noteMutation.isPending ? 'Salvataggio…' : 'Salva nota'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(false);
                          setDraftNote(issue.note);
                          setError(null);
                        }}
                        data-testid="manutenzione-drawer-note-cancel"
                        style={secondaryBtn}
                      >
                        Annulla
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p
                      data-testid="manutenzione-drawer-note"
                      style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        color: '#3D2F24',
                        fontSize: 14,
                        lineHeight: 1.5,
                      }}
                    >
                      {issue.note}
                    </p>
                    {issue.status === 'open' && (
                      <button
                        type="button"
                        onClick={() => setEditing(true)}
                        data-testid="manutenzione-drawer-note-edit"
                        style={{
                          ...secondaryBtn,
                          marginTop: 8,
                        }}
                      >
                        Modifica nota
                      </button>
                    )}
                  </>
                )}
              </section>

              <section style={{ marginBottom: 24 }}>
                <div style={sectionLabel}>Cronologia</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 13 }}>
                  <li style={timelineItem}>
                    <span style={{ color: '#7A6957' }}>Aperto</span>{' '}
                    <strong>{formatItalianDateTime(issue.openedAt)}</strong>
                  </li>
                  {issue.resolvedAt && (
                    <li style={timelineItem}>
                      <span style={{ color: '#7A6957' }}>Risolto</span>{' '}
                      <strong>{formatItalianDateTime(issue.resolvedAt)}</strong>
                    </li>
                  )}
                </ul>
              </section>

              {error && (
                <div
                  data-testid="manutenzione-drawer-error"
                  style={{
                    color: '#A8453A',
                    fontSize: 13,
                    marginBottom: 16,
                    padding: 10,
                    background: '#FFE5DC',
                    borderRadius: 8,
                  }}
                >
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {issue && issue.status === 'open' && (
          <footer
            style={{
              padding: 16,
              borderTop: '1px solid #E8DECF',
              background: '#FAF7F2',
            }}
          >
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Confermi che il guasto e stato risolto?')) {
                  resolveMutation.mutate();
                }
              }}
              disabled={resolveMutation.isPending}
              data-testid="manutenzione-drawer-resolve"
              style={{
                ...primaryBtn,
                width: '100%',
                background: '#4A6B3A',
              }}
            >
              {resolveMutation.isPending ? 'Risoluzione…' : 'Risolvi guasto'}
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}

const sectionLabel: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#7A6957',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 8,
};

const timelineItem: React.CSSProperties = {
  padding: '6px 0',
  borderBottom: '1px solid #F0E8DA',
};

const primaryBtn: React.CSSProperties = {
  padding: '10px 16px',
  background: '#E97B3D',
  color: '#FFF',
  border: 'none',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  padding: '10px 16px',
  background: '#FFF',
  color: '#3D2F24',
  border: '1px solid #D4C4B0',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};
